import { useState } from 'react'
import type {
  DiscoverCardPreviewData,
  FeedCardType,
} from '../../redux-toolkits/discover/discover-card-slice'

const actions: Record<FeedCardType, string> = {
  product: 'Add to cart',
  product_video: 'Add to cart',
  creator_products: 'Add all',
  essentials: 'Add all',
  article: 'Open',
  event: 'RSVP',
  space: 'Directions',
}
const subtitles: Record<FeedCardType, string> = {
  product: 'Discover something good',
  product_video: 'From the creator',
  creator_products: 'Picks from this creator',
  essentials: 'Good Essentials',
  article: 'Good Lists',
  event: 'What’s happening',
  space: 'Spaces & experiences',
}
const money = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  maximumFractionDigits: 2,
})

function previewImages(
  preview: DiscoverCardPreviewData,
): (string | undefined)[] {
  if (preview.video) return [preview.video.thumbnailUrl]
  if (preview.type === 'creator_products')
    return preview.products.map((product) => product.images[0])
  if (preview.products.length) return preview.products[0].images
  return preview.image ? [preview.image] : []
}

export function DiscoverCardPreview({
  preview,
  loading = false,
  error = false,
}: {
  preview?: DiscoverCardPreviewData
  loading?: boolean
  error?: boolean
}) {
  const [imageIndex, setImageIndex] = useState(0)
  if (error)
    return (
      <div
        role="alert"
        className="rounded-2xl border p-8 text-center text-sec-black"
      >
        Unable to load this preview.
      </div>
    )
  if (loading)
    return (
      <div
        role="status"
        className="rounded-2xl bg-gray-bg p-12 text-center animate-pulse"
      >
        Loading preview…
      </div>
    )
  if (!preview)
    return (
      <div className="rounded-2xl border border-dashed p-12 text-center text-sec-black">
        Select content to preview your card.
      </div>
    )

  const images = previewImages(preview)
  const activeIndex = Math.min(imageIndex, Math.max(0, images.length - 1))
  const image = images[activeIndex]
  const creatorName = preview.creator?.name ?? preview.centre?.name ?? 'Good'
  const product =
    preview.type === 'creator_products'
      ? preview.products[activeIndex]
      : preview.products[0]
  const description = preview.description?.replace(/<[^>]*>/g, ' ').trim()

  return (
    <article
      className="overflow-hidden rounded-2xl border border-gray-100 bg-white text-black shadow-sm"
      aria-label={`${preview.title ?? 'Unavailable content'} feed preview`}
    >
      <div className="flex items-center gap-3 p-4">
        {preview.creator?.logo ? (
          <img
            src={preview.creator.logo}
            alt=""
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
            {creatorName.slice(0, 2).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{creatorName}</p>
          <p className="text-xs text-pry-gray">{subtitles[preview.type]}</p>
        </div>
        {preview.creator && (
          <span className="rounded-full border px-3 py-1 text-xs">Follow</span>
        )}
      </div>
      <div className="relative mx-3 overflow-hidden rounded-xl bg-gray-bg">
        {image ? (
          <img
            src={image}
            alt={product?.name ?? preview.title ?? 'Content image'}
            className="aspect-[4/5] w-full object-cover"
          />
        ) : (
          <div className="flex aspect-[4/5] flex-col items-center justify-center gap-4 px-6 text-center text-sec-black">
            <span className="text-4xl" aria-hidden="true">
              {preview.type === 'space' ? '⌖' : '◇'}
            </span>
            <p>{preview.title ?? 'Content is no longer available'}</p>
            {preview.centre && (
              <p className="text-sm">{preview.centre.address}</p>
            )}
          </div>
        )}
        {preview.video && (
          <span className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 text-xs text-white">
            ▶ Video
          </span>
        )}
        {preview.type === 'event' && product?.eventDate && (
          <span className="absolute left-3 top-3 rounded-xl bg-white p-3 text-center text-sm font-medium">
            {new Date(product.eventDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
            })}
          </span>
        )}
        {product && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{product.name}</p>
              <p className="text-xs">{money.format(product.price)}</p>
            </div>
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full bg-black text-white"
              aria-hidden="true"
            >
              +
            </span>
          </div>
        )}
      </div>
      {images.length > 1 && (
        <div
          className="flex justify-center gap-2 py-3"
          aria-label="Carousel images"
        >
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Show image ${index + 1}`}
              aria-pressed={index === activeIndex}
              onClick={() => setImageIndex(index)}
              className={`h-2 w-2 rounded-full ${
                index === activeIndex ? 'bg-black' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div aria-hidden="true" className="flex gap-4 text-2xl">
            <span>♡</span>
            <span>↗</span>
          </div>
          <span className="rounded-full bg-black px-4 py-2 text-xs text-white">
            {actions[preview.type]}
          </span>
        </div>
        <p className="text-sm">
          <span className="font-medium">{creatorName}</span> {preview.title}
        </p>
        {description && (
          <p className="line-clamp-3 text-sm text-sec-black">{description}</p>
        )}
        {preview.type === 'event' && preview.centre && (
          <p className="text-xs text-pry-gray">
            {preview.centre.name} · {preview.centre.address}
          </p>
        )}
      </div>
    </article>
  )
}
